import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import DashboardNav from './DashboardNav';
import LoadingSpinner from './LoadingSpinner';
import './StudentMeter.css';

const StudentMeter = () => {
  const [meterData, setMeterData] = useState({
    summary: {
      morningStrength: 0,
      afternoonStrength: 0,
      totalDailyAttendance: 0,
      totalRegisteredStudents: 0,
      attendanceRate: '0%'
    },
    morningStudents: [],
    afternoonStudents: [],
    busWiseStats: [],
    timeBreakdown: {
      morning: { period: '', count: 0, percentage: '0%' },
      afternoon: { period: '', count: 0, percentage: '0%' }
    }
  });
  const [buses, setBuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    date: new Date().toISOString().split('T')[0],
    busId: ''
  });
  const [activeView, setActiveView] = useState('overview'); // overview, morning, afternoon, buses

  const fetchBuses = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get('/buses', {
        headers: { 'x-auth-token': token }
      });
      const busesData = res.data?.data || res.data || [];
      setBuses(Array.isArray(busesData) ? busesData : []);
    } catch (err) {
      console.error('Error fetching buses:', err.response?.data || err.message);
      setBuses([]);
    }
  }, []);

  const fetchMeterData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams({
        date: filters.date
      });
      
      if (filters.busId) {
        params.append('busId', filters.busId);
      }
      
      const res = await api.get(`/attendance/student-meter?${params.toString()}`, {
        headers: { 'x-auth-token': token }
      });
      
      setMeterData(res.data);
    } catch (err) {
      console.error('Error fetching meter data:', err.response?.data || err.message);
      // Set default data on error
      setMeterData({
        summary: {
          morningStrength: 0,
          afternoonStrength: 0,
          totalDailyAttendance: 0,
          totalRegisteredStudents: 0,
          attendanceRate: '0%'
        },
        morningStudents: [],
        afternoonStudents: [],
        busWiseStats: [],
        timeBreakdown: {
          morning: { period: '12:00 AM - 12:00 PM', count: 0, percentage: '0%' },
          afternoon: { period: '12:00 PM - 11:59 PM', count: 0, percentage: '0%' }
        }
      });
    } finally {
      setLoading(false);
    }
  }, [filters.date, filters.busId]);

  useEffect(() => {
    fetchBuses();
    fetchMeterData();
    
    // Auto-refresh every 5 minutes
    const interval = setInterval(fetchMeterData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleRefresh = () => {
    fetchMeterData();
  };

  const renderStudentList = (students, title, emptyMessage) => (
    <div className="student-list">
      <h4>{title} ({students.length})</h4>
      {students.length === 0 ? (
        <p className="no-data">{emptyMessage}</p>
      ) : (
        <div className="student-grid">
          {students.map((student, index) => (
            <div key={student._id} className="student-card">
              <div className="student-info">
                <span className="student-number">{index + 1}</span>
                <div className="student-details">
                  <strong>{student.studentName}</strong>
                  <div className="student-id">ID: {student.studentId}</div>
                  <div className="bus-info">Bus: {student.busNumber}</div>
                  <div className="time-info">
                    {new Date(student.boardingTime).toLocaleTimeString()}
                  </div>
                  {student.confidence && (
                    <div className="confidence-info">
                      Confidence: {(student.confidence * 100).toFixed(1)}%
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <h1>Student Strength Meter</h1>
        <DashboardNav />
      </header>

      <main className="student-meter-content">
        <section className="controls-section">
          <div className="controls-form">
            <div className="control-group">
              <label>Date:</label>
              <input
                type="date"
                name="date"
                value={filters.date}
                onChange={handleFilterChange}
              />
            </div>
            
            <div className="control-group">
              <label>Bus (Optional):</label>
              <select name="busId" value={filters.busId} onChange={handleFilterChange}>
                <option value="">All Buses</option>
                {buses.map((bus) => (
                  <option key={bus._id} value={bus._id}>
                    {bus.busNumber}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="control-actions">
              <button className="refresh-button" onClick={handleRefresh} disabled={loading}>
                {loading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
          </div>
          
          <div className="auto-refresh-info">
            <span>🔄 Auto-refreshing every 5 minutes</span>
          </div>
        </section>

        {loading ? (
          <div className="loading-container">
            <LoadingSpinner size="large" message="Loading student meter data..." />
          </div>
        ) : (
          <>
            {/* Overview Cards */}
            <section className="overview-section">
              <div className="stats-grid">
                <div className="stat-card morning">
                  <div className="stat-header">
                    <h3>Morning Strength</h3>
                    <span className="period">{meterData.timeBreakdown.morning.period}</span>
                  </div>
                  <div className="stat-number">{meterData.summary.morningStrength}</div>
                  <div className="stat-percentage">{meterData.timeBreakdown.morning.percentage}</div>
                </div>
                
                <div className="stat-card afternoon">
                  <div className="stat-header">
                    <h3>Afternoon Strength</h3>
                    <span className="period">{meterData.timeBreakdown.afternoon.period}</span>
                  </div>
                  <div className="stat-number">{meterData.summary.afternoonStrength}</div>
                  <div className="stat-percentage">{meterData.timeBreakdown.afternoon.percentage}</div>
                </div>
                
                <div className="stat-card total">
                  <div className="stat-header">
                    <h3>Total Daily Attendance</h3>
                    <span className="period">All day</span>
                  </div>
                  <div className="stat-number">{meterData.summary.totalDailyAttendance}</div>
                  <div className="stat-percentage">{meterData.summary.attendanceRate}</div>
                </div>
                
                <div className="stat-card registered">
                  <div className="stat-header">
                    <h3>Registered Students</h3>
                    <span className="period">Total capacity</span>
                  </div>
                  <div className="stat-number">{meterData.summary.totalRegisteredStudents}</div>
                  <div className="stat-percentage">100%</div>
                </div>
              </div>
            </section>

            {/* View Toggle */}
            <section className="view-toggle-section">
              <div className="view-buttons">
                <button 
                  className={`view-button ${activeView === 'overview' ? 'active' : ''}`}
                  onClick={() => setActiveView('overview')}
                >
                  Overview
                </button>
                <button 
                  className={`view-button ${activeView === 'morning' ? 'active' : ''}`}
                  onClick={() => setActiveView('morning')}
                >
                  Morning Students ({meterData.summary.morningStrength})
                </button>
                <button 
                  className={`view-button ${activeView === 'afternoon' ? 'active' : ''}`}
                  onClick={() => setActiveView('afternoon')}
                >
                  Afternoon Students ({meterData.summary.afternoonStrength})
                </button>
                <button 
                  className={`view-button ${activeView === 'buses' ? 'active' : ''}`}
                  onClick={() => setActiveView('buses')}
                >
                  Bus-wise Stats
                </button>
              </div>
            </section>

            {/* Content based on active view */}
            <section className="content-section">
              {activeView === 'overview' && (
                <div className="overview-content">
                  <div className="comparison-chart">
                    <h3>Morning vs Afternoon Comparison</h3>
                    <div className="chart-bars">
                      <div className="bar-container">
                        <div className="bar-label">Morning</div>
                        <div className="bar-wrapper">
                          <div 
                            className="bar morning-bar" 
                            style={{ 
                              width: meterData.summary.totalRegisteredStudents > 0 ? 
                                `${(meterData.summary.morningStrength / meterData.summary.totalRegisteredStudents) * 100}%` : 
                                '0%' 
                            }}
                          ></div>
                        </div>
                        <div className="bar-value">{meterData.summary.morningStrength}</div>
                      </div>
                      <div className="bar-container">
                        <div className="bar-label">Afternoon</div>
                        <div className="bar-wrapper">
                          <div 
                            className="bar afternoon-bar" 
                            style={{ 
                              width: meterData.summary.totalRegisteredStudents > 0 ? 
                                `${(meterData.summary.afternoonStrength / meterData.summary.totalRegisteredStudents) * 100}%` : 
                                '0%' 
                            }}
                          ></div>
                        </div>
                        <div className="bar-value">{meterData.summary.afternoonStrength}</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeView === 'morning' && renderStudentList(
                meterData.morningStudents, 
                'Morning Students', 
                'No students boarded in the morning.'
              )}

              {activeView === 'afternoon' && renderStudentList(
                meterData.afternoonStudents, 
                'Afternoon Students', 
                'No students boarded in the afternoon.'
              )}

              {activeView === 'buses' && (
                <div className="bus-stats">
                  <h3>Bus-wise Statistics</h3>
                  {meterData.busWiseStats.length === 0 ? (
                    <p className="no-data">No bus statistics available for the selected date.</p>
                  ) : (
                    <div className="bus-stats-grid">
                      {meterData.busWiseStats.map((bus) => (
                        <div key={bus._id} className="bus-stat-card">
                          <h4>Bus {bus.busNumber}</h4>
                          <div className="bus-metrics">
                            <div className="metric">
                              <span className="metric-label">Total Boarded:</span>
                              <span className="metric-value">{bus.totalBoarded}</span>
                            </div>
                            <div className="metric">
                              <span className="metric-label">Total Deboarded:</span>
                              <span className="metric-value">{bus.totalDeboarded}</span>
                            </div>
                            <div className="metric">
                              <span className="metric-label">Morning:</span>
                              <span className="metric-value">{bus.morningBoarded}</span>
                            </div>
                            <div className="metric">
                              <span className="metric-label">Afternoon:</span>
                              <span className="metric-value">{bus.afternoonBoarded}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
};

export default StudentMeter;